import { Injectable, OnDestroy } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { createSelector, Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AppState } from 'src/app/app.state';
import { selectAppConfig } from 'src/app/core/application-config/application-config.selectors';
import { routeTo } from 'src/app/core/routing/routing.actions';
import { selectOrderTicketTypes } from 'src/app/core/user/user.selectors';
import { OrderTicketType } from 'src/app/core/user/user.state';
import { APPCONFIG_ACCESSO_ID } from 'src/app/shared/enums/application-config.enums';

const DEFAULT_ROUTES = {
	isBookable: ['booking-portal', 'summary'],
	isLoqBookable: ['booking-portal', 'summary'],
	isMembership: ['account', 'overview'],
	isPaymentPlan: ['account', 'overview'],
};

@Injectable({
	providedIn: 'root',
})
export class OrderTypeGuard implements OnDestroy {
	private _orderTicketTypes: OrderTicketType;
	private _accessoIdEnabled: boolean;
	private _destroy$ = new Subject<void>();

	/**
	 * the constructor
	 * @param _store$ our store
	 */
	constructor(private _store$: Store<AppState>) {
		this._store$
			.select(
				createSelector(
					selectOrderTicketTypes,
					selectAppConfig([APPCONFIG_ACCESSO_ID]),
					(orderTicketTypes, config) => {
						return {
							orderTicketTypes,
							config,
						};
					}
				)
			)
			.pipe(takeUntil(this._destroy$))
			.subscribe(({ orderTicketTypes, config }) => {
				this._accessoIdEnabled = config?.accesso_id?.enable;
				// we only want to get ticket types again if they are actually updated
				if (orderTicketTypes) {
					this._orderTicketTypes = orderTicketTypes;
				}
			});
	}

	/**
	 * allows route to activate if the order's type matches the route's order type requirements
	 * @param route the activate route snapshot
	 * @param state the router state
	 */
	public canActivate(
		route: ActivatedRouteSnapshot,
		state: RouterStateSnapshot
	): Observable<boolean> | Promise<boolean> | boolean {
		if (state.url.includes('/dashboard') && !this._accessoIdEnabled) {
			this._store$.dispatch(
				routeTo({
					payload: ['login'],
				})
			);
		}
		// This can be expanded as we add more scenarios/types
		if (
			(route.data.requiredOrderTypes?.isMembership || route.data.requiredOrderTypes?.isPaymentPlan) &&
			!this._orderTicketTypes.isMembership &&
			!this._orderTicketTypes.isPaymentPlan
		) {
			// this order does not have account information, check for other types
			if (this._orderTicketTypes.isBookable) {
				this._store$.dispatch(
					routeTo({
						payload: DEFAULT_ROUTES.isBookable,
					})
				);
			}
		}

		if (route.data.requiredOrderTypes?.isBookable && !this._orderTicketTypes.isBookable) {
			// this order does not have bookings, check for other types
			if (this._orderTicketTypes.isMembership) {
				this._store$.dispatch(
					routeTo({
						payload: DEFAULT_ROUTES.isMembership,
					})
				);
			}

			if (this._orderTicketTypes.isPaymentPlan) {
				this._store$.dispatch(
					routeTo({
						payload: DEFAULT_ROUTES.isPaymentPlan,
					})
				);
			}
		}

		if (route.data.requiredOrderTypes?.isNewContractViewer && !this._orderTicketTypes.isNewContractViewer) {
			// this merchant isn't setup for new contract viewer, route to overview
			if (this._orderTicketTypes.isPaymentPlan) {
				this._store$.dispatch(
					routeTo({
						payload: DEFAULT_ROUTES.isPaymentPlan,
					})
				);
			}
		}

		return true;
	}

	public ngOnDestroy() {
		this._destroy$.next();
		this._destroy$.complete();
	}
}
